import { Box, Chip, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useState } from "react";
import AdminTable from "../Components/Items/AdminTable";
import LayoutTable from "../Components/Layouts/LayoutTable";
import {
  useGetAllConnectionPointsOptionsQuery,
  useGetAllConnectionPointsQuery,
  useGetAllShapesQuery,
  useGetAllZoneQuery,
} from "../Slices/roomNateConstantApis";
import { useGlobalAuthContext } from "../context/AuthContext";
import { useGlobalContext } from "../context/GlobalContext";
import { usePartnersQuery } from "../Slices/roomNateApis";
import CustomSwitch from "../Components/GlobalStyles/CustomSwitch";
import Cookies from "js-cookie";

// iOS-style segment control sx — reuse in both toggles
const iosSegmentSx = {
  bgcolor: "rgba(118,118,128,0.12)",
  borderRadius: "9px",
  p: "2px",
  "& .MuiToggleButtonGroup-grouped": {
    border: "none !important",
    borderRadius: "7px !important",
  },
  "& .MuiToggleButton-root": {
    border: "none",
    borderRadius: "7px",
    px: 2.5,
    py: 0.75,
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: "text.secondary",
    textTransform: "none",
    letterSpacing: "-0.01em",
    lineHeight: 1.4,
    minWidth: 72,
    "&.Mui-selected": {
      bgcolor: "white",
      color: "text.primary",
      fontWeight: 600,
      boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 1px rgba(0,0,0,0.06)",
      "&:hover": { bgcolor: "white" },
    },
    "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
  },
};

const AdminDashboard = () => {
  const { currentUserSessionStorage } = useGlobalAuthContext();
  const { activePartner, setActivePartner } = useGlobalContext();
  const [gridView, setGridView] = useState(false);
  // Default to "layouts" per spec
  const [activeTab, setActiveTab] = useState("layouts");

  // Resolve partner name — hits RTK Query cache (LayoutTable already fetches this)
  const { data: partnersData } = usePartnersQuery("", {
    skip: !currentUserSessionStorage,
  });
  const activePartnerTitle = activePartner
    ? partnersData?.find((p) => p._id === activePartner)?.title ?? "…"
    : null;

  const clearPartner = () => {
    setActivePartner("");
    Cookies.set("activePartner", "", { expires: 30 });
  };

  const { data: zonesData } = useGetAllZoneQuery("", {
    skip: !currentUserSessionStorage,
  });
  const { data: shapesData } = useGetAllShapesQuery("", {
    skip: !currentUserSessionStorage,
  });
  const { data: connectionPointsOptionsData } =
    useGetAllConnectionPointsOptionsQuery("", {
      skip: !currentUserSessionStorage,
    });
  const { data: connectionPointsData } = useGetAllConnectionPointsQuery("", {
    skip: !currentUserSessionStorage,
  });

  const sharedProps = {
    zonesData,
    shapesData,
    connectionPointsData,
    connectionPointsOptionsData,
    gridView,
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        {/* iOS segment control */}
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={(_, v) => { if (v !== null) setActiveTab(v); }}
          sx={iosSegmentSx}
        >
          <ToggleButton value="items">Items</ToggleButton>
          <ToggleButton value="layouts">Layouts</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
            Grid View
          </Typography>
          <CustomSwitch
            checked={gridView}
            onClick={() => setGridView((v) => !v)}
          />
        </Box>
      </Box>

      {/* Partner context indicator — shown above zone/sort filters */}
      {activePartnerTitle ? (
        <Chip
          label={`Partner: ${activePartnerTitle}`}
          onDelete={clearPartner}
          size="small"
          sx={{
            mb: 2,
            fontWeight: 600,
            fontSize: "0.78rem",
            bgcolor: "rgba(0,0,0,0.06)",
            "& .MuiChip-deleteIcon": { fontSize: "1rem" },
          }}
        />
      ) : (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: "block", mb: 2, fontSize: "0.75rem" }}
        >
          Showing all partners
        </Typography>
      )}

      {activeTab === "items" && <AdminTable {...sharedProps} />}
      {activeTab === "layouts" && <LayoutTable {...sharedProps} />}
    </Box>
  );
};

export default AdminDashboard;
